import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ClockIcon, BuildingIcon, UserIcon } from '@/components/icons';
import { useI18n } from '@/i18n/I18nProvider';

interface CharterTermsViewProps {
  isExpatCitizenship: boolean;
}

export function CharterTermsView({ isExpatCitizenship }: CharterTermsViewProps) {
  const { t } = useI18n();
  const conductTerms = t.parentConduct.conductTerms;

  const parentSectionsAll = conductTerms.parentCommitments.sections;
  const parentSections = isExpatCitizenship
    ? parentSectionsAll
    : parentSectionsAll.slice(0, Math.max(0, parentSectionsAll.length - 1));

  return (
    <div className="space-y-6">
      {/* Validity Period */}
      <Card className="border border-primary/20 shadow-sm bg-primary/5">
        <CardContent className="pt-4">
          <div className="flex items-center gap-3">
            <ClockIcon />
            <div>
              <p className="font-semibold text-foreground">{t.parentConduct.termsSection.validityTitle}</p>
              <p className="text-muted-foreground">{conductTerms.validityPeriod}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* School Commitments */}
      <Card className="border shadow-sm">
        <CardHeader className="rounded-t-xl bg-primary text-primary-foreground">
          <CardTitle className="text-base flex items-center">
            <BuildingIcon className="me-2 text-lg" />
            {t.parentConduct.termsSection.schoolCommitmentsTitle}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="max-h-[28rem] overflow-y-auto border rounded-xl p-5 bg-background shadow-inner space-y-6">
            {conductTerms.schoolCommitments.sections.map((section: any, sectionIndex: number) => (
              <div key={sectionIndex} className="space-y-3">
                <h4 className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">
                    {sectionIndex + 1}
                  </span>
                  <span>{section.title}</span>
                </h4>
                <ul className="space-y-2.5 ps-3">
                  {section.items.map((item: any, itemIndex: number) => (
                    <li
                      key={itemIndex}
                      className="flex items-start gap-2.5 text-muted-foreground text-sm leading-relaxed"
                    >
                      <span className="shrink-0 w-1.5 h-1.5 bg-primary/70 rounded-full mt-2" />
                      <span className="flex-1">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Parent Commitments */}
      <Card className="border shadow-sm">
        <CardHeader className="rounded-t-xl bg-primary text-primary-foreground">
          <CardTitle className="text-base flex items-center">
            <UserIcon className="me-2 text-lg" />
            {t.parentConduct.termsSection.parentCommitmentsTitle}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="max-h-[28rem] overflow-y-auto border rounded-xl p-5 bg-background shadow-inner space-y-6">
            {parentSections.map((section: any, sectionIndex: number) => (
              <div key={sectionIndex} className="space-y-3">
                <h4 className="text-sm font-semibold text-foreground flex items-center gap-2.5">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">
                    {sectionIndex + 1}
                  </span>
                  <span>{section.title}</span>
                </h4>
                <ul className="space-y-2.5 ps-3">
                  {section.items.map((item: any, itemIndex: number) => (
                    <li
                      key={itemIndex}
                      className="flex items-start gap-2.5 text-muted-foreground text-sm leading-relaxed"
                    >
                      <span className="shrink-0 w-1.5 h-1.5 bg-primary/70 rounded-full mt-2" />
                      <span className="flex-1">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
